import React, { useState, useEffect, useRef, useCallback } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate } from "react-router-dom";
import HotelDmcTabs from "./HotelDmcTabs";
import roomTypesData from "../../Data/RoomTypes.json";

const HotelsMasterSearch = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    
    // Search states
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);
    
    // Hotel selection and details
    const [selectedHotel, setSelectedHotel] = useState(null);
    const [favorites, setFavorites] = useState([]);
    
    // Filter and sort states
    const [filters, setFilters] = useState({
        minStars: 0,
        maxStars: 5,
        accommodationType: "",
        country: ""
    });
    const [sortBy, setSortBy] = useState("relevance");
    const [sortOrder, setSortOrder] = useState("asc");
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [resultsPerPage] = useState(12);
    const [totalResults, setTotalResults] = useState(0);
    
    // UI states
    const [showFilters, setShowFilters] = useState(false);
    const [showHotelDetails, setShowHotelDetails] = useState(false);
    
    // Tabs state
    const [activeTab, setActiveTab] = useState("roomDetails");
    
    // Room details state
    const [roomDetails, setRoomDetails] = useState([]);
    const [showAddRoomForm, setShowAddRoomForm] = useState(false);
    const [newRoom, setNewRoom] = useState({
        roomType: "",
        mealPlan: "CPAI",
        extraBedPrice: "",
        childWithoutBedPrice: "",
        price: "",
        currency: "USD",
        validFrom: "",
        validTo: ""
    });

    // NEW: Global validity dates for all room types
    const [globalValidFrom, setGlobalValidFrom] = useState("");
    const [globalValidTo, setGlobalValidTo] = useState("");

    // Room type options from JSON - Convert to uppercase
    const roomTypeOptions = roomTypesData.roomTypes.map(type => type.toUpperCase());
    
    // Auto-complete states for room type
    const [roomTypeSuggestions, setRoomTypeSuggestions] = useState([]);
    const [showRoomTypeDropdown, setShowRoomTypeDropdown] = useState(false);
    const [roomTypeSearchTerm, setRoomTypeSearchTerm] = useState("");

    // Meal plan options
    const mealPlanOptions = [
        { value: "CPAI", label: "CPAI → Stay + Breakfast" },
        { value: "MAPAI", label: "MAPAI → Stay + Breakfast + Lunch/Dinner" },
        { value: "APAI", label: "APAI → Stay + Breakfast + Lunch + Dinner" }
    ];

    // Currency options
    const currencyOptions = [
        { code: "USD", symbol: "$", name: "US Dollar" },
        { code: "EUR", symbol: "€", name: "Euro" },
        { code: "GBP", symbol: "£", name: "British Pound" },
        { code: "INR", symbol: "₹", name: "Indian Rupee" },
        { code: "AED", symbol: "AED", name: "UAE Dirham" },
        { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
        { code: "AUD", symbol: "A$", name: "Australian Dollar" },
        { code: "CAD", symbol: "C$", name: "Canadian Dollar" }
    ];

    // Amenities state - Point based
    const [amenities, setAmenities] = useState([]);
    const [newAmenityPoint, setNewAmenityPoint] = useState("");

    // Terms & Conditions state - Points system
    const [termsAndConditions, setTermsAndConditions] = useState([]);
    const [newTermPoint, setNewTermPoint] = useState("");

    // Cancellation Policy state - Points system
    const [cancellationPolicy, setCancellationPolicy] = useState([]);
    const [newCancellationPoint, setNewCancellationPoint] = useState("");

    // Child Policy state - Points system
    const [childPolicy, setChildPolicy] = useState([]);
    const [newChildPolicyPoint, setNewChildPolicyPoint] = useState("");

    // Bank Details state
    const [bankDetails, setBankDetails] = useState([]);
    const [showAddBankForm, setShowAddBankForm] = useState(false);
    const [newBankDetail, setNewBankDetail] = useState({
        bankName: "",
        accountNumber: "",
        swiftIfscCode: ""
    });

    // Contact details state
    const [showAddContactForm, setShowAddContactForm] = useState(false);
    const [newContact, setNewContact] = useState({
        contactName: "",
        email: "",
        phone: "",
        whatsapp: ""
    });

    // Preview state
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    
    // Save state
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveError, setSaveError] = useState("");

    const searchInputRef = useRef(null);
    const resultsRef = useRef(null);
    const roomTypeInputRef = useRef(null);
    const searchDropdownRef = useRef(null);

    // FIXED: Close dropdown when clicking outside - more robust implementation
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside search dropdown
            if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target) && 
                searchInputRef.current && !searchInputRef.current.contains(event.target)) {
                setShowSearchDropdown(false);
            }
            
            // Check if click is outside room type dropdown
            if (roomTypeInputRef.current && !roomTypeInputRef.current.contains(event.target) && 
                !event.target.closest('.room-type-dropdown')) {
                setShowRoomTypeDropdown(false);
            }
        };

        // Handle scroll to close dropdowns
        const handleScroll = () => {
            if (showSearchDropdown) {
                setShowSearchDropdown(false);
            }
            if (showRoomTypeDropdown) {
                setShowRoomTypeDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("scroll", handleScroll, true); // Use capture phase
        
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [showSearchDropdown, showRoomTypeDropdown]);

    // Debounce search term for suggestions
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchTerm.trim().length >= 2) {
                fetchSearchSuggestions();
            } else {
                setSearchSuggestions([]);
                setShowSearchDropdown(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // SINGLE API CALL - Fetch search suggestions with complete hotel data
    const fetchSearchSuggestions = async () => {
        setIsSearching(true);
        try {
            const response = await apiService.post(
                `${config.MASTERHOTELS}?searchName=${encodeURIComponent(searchTerm)}&limit=10`,
                null
            );
            
            // API returns complete hotel data
            const hotels = response.data || response || [];
            
            setSearchSuggestions(hotels);
            setShowSearchDropdown(true);
        } catch (error) {
            console.error("Error fetching search suggestions:", error);
            setSearchSuggestions([]);
        } finally {
            setIsSearching(false);
        }
    };

    // NEW FUNCTION: Save hotel data to API
    const saveHotelData = async () => {
        if (!selectedHotel || !user) {
            setSaveError("No hotel selected or user not authenticated");
            return;
        }

        setIsSaving(true);
        setSaveError("");
        setSaveSuccess(false);

        try {
            // Prepare contact info from selectedHotel or use empty object
            const contactInfo = {
                contactName: selectedHotel.contactName || user.name || "",
                email: selectedHotel.email || user.email || "",
                phone: selectedHotel.phone || user.phoneNumber || "",
                whatsApp: selectedHotel.whatsapp || user.whatsAppNumber || ""
            };

            // Prepare room details with correct structure - use global validity dates
            const roomDetailsDto = roomDetails.map(room => ({
                roomType: room.roomType,
                mealPlan: room.mealPlan,
                price: parseFloat(room.price) || 0,
                currency: room.currency,
                extraBedPrice: room.extraBedPrice ? parseFloat(room.extraBedPrice) : null,
                childWithoutBedPrice: room.childWithoutBedPrice ? parseFloat(room.childWithoutBedPrice) : null,
                validFrom: globalValidFrom || null,
                validTo: globalValidTo || null
            }));

            // Prepare amenities with correct structure
            const amenitiesDto = amenities.map(amenity => ({
                point: amenity.point
            }));

            // Prepare terms and conditions
            const termsDto = termsAndConditions.map(term => ({
                point: term.point
            }));

            // Prepare cancellation policy
            const cancellationDto = cancellationPolicy.map(policy => ({
                point: policy.point
            }));

            // Prepare child policy
            const childPolicyDto = childPolicy.map(policy => ({
                point: policy.point
            }));

            // Prepare bank details
            const bankDetailsDto = bankDetails.map(bank => ({
                bankName: bank.bankName,
                accountNumber: bank.accountNumber,
                swiftIfscCode: bank.swiftIfscCode || ""
            }));

            // Prepare the DTO for API
            const companyHotelDto = {
                hotelId: selectedHotel.hotelId,
                isOwner: true,
                contactInfo: contactInfo,
                roomDetails: roomDetailsDto,
                amenities: amenitiesDto,
                termsAndConditions: termsDto,
                cancellationPolicy: cancellationDto,
                childPolicy: childPolicyDto,
                bankDetails: bankDetailsDto
            };

            // Prepare the request payload
            const saveRequest = {
                userId: user.id,
                companyId: user.cid,
                dto: companyHotelDto
            };

            console.log("Saving hotel data:", saveRequest);

            // Call the API
            const response = await apiService.post(
                `${config.UPDATEHOTELDATA}`,
                saveRequest
            );

            if (response && response.success) {
                setSaveSuccess(true);
                setTimeout(() => setSaveSuccess(false), 3000);
                console.log("Hotel saved successfully:", response.data);
            } else {
                setSaveError(response.message || "Failed to save hotel data");
            }
        } catch (error) {
            console.error("Error saving hotel data:", error);
            setSaveError(error.response?.data?.message || error.message || "Error saving hotel data");
        } finally {
            setIsSaving(false);
        }
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
    };

    // Select hotel from search suggestions - DIRECTLY DISPLAY THE HOTEL DATA
    const selectSearchSuggestion = (hotel) => {
        setSearchTerm(hotel.hotelName);
        setSearchSuggestions([]);
        setShowSearchDropdown(false);
        
        // Display the selected hotel in results below
        setSearchResults([hotel]);
        setTotalResults(1);
        setCurrentPage(1);
        setShowResults(true);
        
        // Scroll to results
        setTimeout(() => {
            if (resultsRef.current) {
                resultsRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    };

    // Search all hotels function (when user presses Enter or clicks Search All)
    const searchAllHotels = async () => {
        if (searchTerm.trim().length < 2) return;
        
        setIsSearching(true);
        
        try {
            const response = await apiService.post(
                `${config.MASTERHOTELS}?searchName=${encodeURIComponent(searchTerm)}&limit=20`,
                null
            );
            
            const hotels = response.data || response || [];
            
            setSearchResults(hotels);
            setTotalResults(hotels.length || 0);
            setCurrentPage(1);
            setShowResults(true);
            
            if (resultsRef.current) {
                resultsRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        } catch (error) {
            console.error("Error searching hotels:", error);
        } finally {
            setIsSearching(false);
        }
    };

    // Handle search form submit
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchTerm.trim().length >= 2) {
            searchAllHotels();
        }
    };

    // Clear search
    const clearSearch = () => {
        setSearchTerm("");
        setSearchResults([]);
        setShowResults(false);
        setSelectedHotel(null);
        setShowHotelDetails(false);
        setShowFilters(false);
        resetFilters();
        setRoomDetails([]);
        setAmenities([]);
        setTermsAndConditions([]);
        setCancellationPolicy([]);
        setChildPolicy([]);
        setBankDetails([]);
        setShowAddRoomForm(false);
        setShowAddContactForm(false);
        setActiveTab("roomDetails");
        setNewContact({
            contactName: "",
            email: "",
            phone: "",
            whatsapp: ""
        });
        setNewAmenityPoint("");
        setNewTermPoint("");
        setNewCancellationPoint("");
        setNewChildPolicyPoint("");
        setShowPreviewModal(false);
        setRoomTypeSearchTerm("");
        setRoomTypeSuggestions([]);
        setShowRoomTypeDropdown(false);
        setSearchSuggestions([]);
        setShowSearchDropdown(false);
        setSaveSuccess(false);
        setSaveError("");
        setGlobalValidFrom("");
        setGlobalValidTo("");
    };

    // Handle hotel selection from results list
    const handleHotelSelect = (hotel) => {
        setSelectedHotel(hotel);
        setShowHotelDetails(true);
        
        // Reset all forms when selecting a new hotel
        setRoomDetails([]);
        setAmenities([]);
        setTermsAndConditions([]);
        setCancellationPolicy([]);
        setChildPolicy([]);
        setBankDetails([]);
        setShowAddRoomForm(false);
        setShowAddContactForm(false);
        setActiveTab("roomDetails");
        setNewAmenityPoint("");
        setNewTermPoint("");
        setNewCancellationPoint("");
        setNewChildPolicyPoint("");
        setShowPreviewModal(false);
        setRoomTypeSearchTerm("");
        setRoomTypeSuggestions([]);
        setShowRoomTypeDropdown(false);
        setSaveSuccess(false);
        setSaveError("");
        setGlobalValidFrom("");
        setGlobalValidTo("");
        
        // Initialize contact info with user data if available
        if (user) {
            setNewContact({
                contactName: user.name || "",
                email: user.email || "",
                phone: user.phoneNumber || "",
                whatsapp: user.whatsAppNumber || ""
            });
        }
        
        // Scroll to hotel details
        setTimeout(() => {
            const detailsSection = document.getElementById('hotel-details-section');
            if (detailsSection) {
                detailsSection.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    };

    // Close hotel details
    const closeHotelDetails = () => {
        setShowHotelDetails(false);
        setSelectedHotel(null);
        setRoomDetails([]);
        setAmenities([]);
        setTermsAndConditions([]);
        setCancellationPolicy([]);
        setChildPolicy([]);
        setBankDetails([]);
        setShowAddRoomForm(false);
        setShowAddContactForm(false);
        setActiveTab("roomDetails");
        setNewAmenityPoint("");
        setNewTermPoint("");
        setNewCancellationPoint("");
        setNewChildPolicyPoint("");
        setShowPreviewModal(false);
        setRoomTypeSearchTerm("");
        setRoomTypeSuggestions([]);
        setShowRoomTypeDropdown(false);
        setSaveSuccess(false);
        setSaveError("");
        setGlobalValidFrom("");
        setGlobalValidTo("");
    };

    // Share via WhatsApp Web
    const shareViaWhatsApp = () => {
        if (!selectedHotel) return;

        const hotelName = selectedHotel.hotelName || "Hotel";
        const stars = selectedHotel.starCategory || "";
        const accommodationType = selectedHotel.accommdationType || "Hotel";
        
        const address = selectedHotel.address || "";
        const city = selectedHotel.city || "";
        const state = selectedHotel.state || "";
        const country = selectedHotel.country || "";
        
        const contactName = selectedHotel.contactName || user?.name || "";
        const phone = selectedHotel.phone || user?.phoneNumber || "";
        const whatsapp = selectedHotel.whatsapp || user?.whatsAppNumber || "";
        const email = selectedHotel.email || user?.email || "";
        
        const latitude = selectedHotel.latitude || "";
        const longitude = selectedHotel.longitude || "";

        let message = `Dear Agents,\n\n`;
        message += `Hope you are doing well.\n\n`;
        message += `✨ *Attractive Offers Available*\n\n`;
        message += `🏨 *${hotelName}*`;
        if (stars) message += ` (${stars}⭐ ${accommodationType})`;
        message += `\n\n`;
        
        if (roomDetails.length > 0) {
            message += `*Room Details*\n`;
            roomDetails.forEach((room, index) => {
                const currencySymbol = getCurrencySymbol(room.currency);
                const mealPlanLabel = mealPlanOptions.find(mp => mp.value === room.mealPlan)?.label || room.mealPlan;
                
                message += `${room.roomType} | ${mealPlanLabel}\n`;
                message += `Rate: ${currencySymbol}${room.price}\n`;
                if (room.extraBedPrice) {
                    message += `Extra Bed: ${currencySymbol}${room.extraBedPrice}\n`;
                }
                if (room.childWithoutBedPrice) {
                    message += `Child without bed: ${currencySymbol}${room.childWithoutBedPrice}\n`;
                }
                message += `\n`;
            });
            
            // Add global validity dates once at the end
            if (globalValidFrom && globalValidTo) {
                message += `Valid for all rooms: ${formatDate(globalValidFrom)} to ${formatDate(globalValidTo)}\n\n`;
            }
        }
        
        message += `📍 *Location*\n`;
        if (address) message += `${address}`;
        if (city) message += `, ${city}`;
        if (state) message += `, ${state}`;
        if (country) message += `, ${country}`;
        message += `\n\n`;

        if (latitude && longitude) {
            message += `https://www.google.com/maps?q=${latitude},${longitude}\n\n`;
        }
        
        message += `📞 *Contact*\n`;
        if (contactName) message += `${contactName}\n`;
        if (phone) message += `${phone}\n`;
        if (email) message += `${email}\n\n`;
        message += `Please share your enquiries for the best available rates.\n`;
        message += `We look forward to working with you.\n\n`;
        message += `Best regards.`;
        
        const encodedMessage = encodeURIComponent(message);
        window.open(`https://web.whatsapp.com/send?text=${encodedMessage}`, "_blank");
    };

    const shareViaEmail = () => {
        if (!selectedHotel) return;

        const hotelName = selectedHotel.hotelName || "Hotel";
        const stars = selectedHotel.starCategory || "";
        const accommodationType = selectedHotel.accommdationType || "Hotel";
        
        const address = selectedHotel.address || "";
        const city = selectedHotel.city || "";
        const state = selectedHotel.state || "";
        const country = selectedHotel.country || "";
        
        const contactName = selectedHotel.contactName || user?.name || "";
        const phone = selectedHotel.phone || user?.phoneNumber || "";
        const whatsapp = selectedHotel.whatsapp || user?.whatsAppNumber || "";
        const email = selectedHotel.email || user?.email || "";
        
        const latitude = selectedHotel.latitude || "";
        const longitude = selectedHotel.longitude || "";

        const subject = `✨ Attractive Offers - ${hotelName} ${stars ? stars + '⭐' : ''}`;

        let body = `<html><body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 20px;">`;
        
        // Header with hotel info
        body += `<div style="text-align: center; margin-bottom: 30px; padding: 20px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 10px; color: white;">`;
        body += `<h1 style="margin: 0 0 10px 0; font-size: 28px;">${hotelName}</h1>`;
        if (stars) {
            body += `<div style="font-size: 20px; margin-bottom: 10px;">`;
            for (let i = 1; i <= 5; i++) {
                if (i <= stars) {
                    body += `★`;
                } else {
                    body += `☆`;
                }
            }
            body += ` ${accommodationType}</div>`;
        }
        body += `<p style="margin: 0; font-size: 18px; opacity: 0.9;">Exclusive Hotel Offer</p>`;
        body += `</div>`;
        
        // Introduction
        body += `<div style="text-align: center; margin-bottom: 30px;">`;
        body += `<h2 style="color: #2c3e50; margin-bottom: 15px;">✨ Attractive Offers Available ✨</h2>`;
        body += `<p style="font-size: 16px; color: #555;">Hope you are doing well. We are pleased to present you with exclusive rates for this property.</p>`;
        body += `</div>`;
        
        // Room Details Section
        if (roomDetails.length > 0) {
            body += `<div style="margin-bottom: 30px; border: 2px solid #3498db; border-radius: 10px; overflow: hidden;">`;
            body += `<div style="background: #3498db; color: white; padding: 15px; font-size: 18px; font-weight: bold;">🏨 Room Details & Rates</div>`;
            body += `<div style="padding: 20px;">`;
            
            roomDetails.forEach((room, index) => {
                const currencySymbol = getCurrencySymbol(room.currency);
                const mealPlanLabel = mealPlanOptions.find(mp => mp.value === room.mealPlan)?.label || room.mealPlan;
                
                body += `<div style="margin-bottom: 20px; padding: 15px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #3498db;">`;
                body += `<h3 style="color: #2c3e50; margin: 0 0 10px 0;">${room.roomType}</h3>`;
                body += `<p style="margin: 5px 0; color: #555;"><strong>Meal Plan:</strong> ${mealPlanLabel}</p>`;
                body += `<p style="margin: 5px 0; color: #555;"><strong>Rate:</strong> <span style="color: #e74c3c; font-weight: bold; font-size: 18px;">${currencySymbol}${room.price}</span> per room per night</p>`;
                
                if (room.extraBedPrice || room.childWithoutBedPrice) {
                    body += `<div style="margin-top: 10px; padding-top: 10px; border-top: 1px dashed #ddd;">`;
                    if (room.extraBedPrice) {
                        body += `<p style="margin: 5px 0; color: #27ae60;"><strong>Extra Bed:</strong> ${currencySymbol}${room.extraBedPrice}</p>`;
                    }
                    if (room.childWithoutBedPrice) {
                        body += `<p style="margin: 5px 0; color: #e67e22;"><strong>Child without bed:</strong> ${currencySymbol}${room.childWithoutBedPrice}</p>`;
                    }
                    body += `</div>`;
                }
                body += `</div>`;
            });
            
            // Add global validity dates once
            if (globalValidFrom && globalValidTo && globalValidFrom !== "Not specified" && globalValidTo !== "Not specified") {
                body += `<p style="margin: 15px 0 5px 0; color: #7f8c8d; font-size: 14px;"><strong>Valid for all rooms:</strong> ${formatDate(globalValidFrom)} to ${formatDate(globalValidTo)}</p>`;
            }
            
            body += `</div></div>`;
        }
        
        // Location Section
        body += `<div style="margin-bottom: 30px; border: 2px solid #2ecc71; border-radius: 10px; overflow: hidden;">`;
        body += `<div style="background: #2ecc71; color: white; padding: 15px; font-size: 18px; font-weight: bold;">📍 Location</div>`;
        body += `<div style="padding: 20px;">`;
        body += `<p style="margin: 10px 0;"><strong>Address:</strong> ${address || 'Not Available'}</p>`;
        if (city || state || country) {
            body += `<p style="margin: 10px 0;"><strong>Location:</strong> ${[city, state, country].filter(Boolean).join(', ')}</p>`;
        }
        if (latitude && longitude) {
            body += `<p style="margin: 10px 0;"><a href="https://www.google.com/maps?q=${latitude},${longitude}" target="_blank" style="color: #3498db; text-decoration: none;">📍 View on Google Maps</a></p>`;
        }
        body += `</div></div>`;
        
        // Contact Section
        body += `<div style="margin-bottom: 30px; border: 2px solid #9b59b6; border-radius: 10px; overflow: hidden;">`;
        body += `<div style="background: #9b59b6; color: white; padding: 15px; font-size: 18px; font-weight: bold;">📞 Contact Information</div>`;
        body += `<div style="padding: 20px;">`;
        if (contactName) body += `<p style="margin: 10px 0;"><strong>Contact Person:</strong> ${contactName}</p>`;
        if (phone) body += `<p style="margin: 10px 0;"><strong>Phone:</strong> ${phone}</p>`;
        if (whatsapp) body += `<p style="margin: 10px 0;"><strong>WhatsApp:</strong> ${whatsapp}</p>`;
        if (email) body += `<p style="margin: 10px 0;"><strong>Email:</strong> <a href="mailto:${email}" style="color: #3498db;">${email}</a></p>`;
        body += `</div></div>`;
        
        // Additional Information Sections
        if (amenities.length > 0) {
            body += `<div style="margin-bottom: 30px; border: 2px solid #f39c12; border-radius: 10px; overflow: hidden;">`;
            body += `<div style="background: #f39c12; color: white; padding: 15px; font-size: 18px; font-weight: bold;">⭐ Hotel Amenities</div>`;
            body += `<div style="padding: 20px;">`;
            body += `<ul style="margin: 0; padding-left: 20px;">`;
            amenities.forEach((amenity, index) => {
                body += `<li style="margin: 8px 0; color: #555;">${amenity.point}</li>`;
            });
            body += `</ul>`;
            body += `</div></div>`;
        }
        
        // Cancellation Policy
        if (cancellationPolicy.length > 0) {
            body += `<div style="margin-bottom: 30px; border: 2px solid #e74c3c; border-radius: 10px; overflow: hidden;">`;
            body += `<div style="background: #e74c3c; color: white; padding: 15px; font-size: 18px; font-weight: bold;">⚠️ Cancellation Policy</div>`;
            body += `<div style="padding: 20px;">`;
            body += `<ul style="margin: 0; padding-left: 20px;">`;
            cancellationPolicy.forEach((point, index) => {
                body += `<li style="margin: 8px 0; color: #555;">${point.point}</li>`;
            });
            body += `</ul>`;
            body += `</div></div>`;
        }
        
        // Call to Action
        body += `<div style="text-align: center; margin-top: 40px; padding: 30px; background: #ecf0f1; border-radius: 10px;">`;
        body += `<h3 style="color: #2c3e50; margin-bottom: 20px;">Ready to Book?</h3>`;
        body += `<p style="font-size: 16px; color: #555; margin-bottom: 25px;">Please share your enquiries for the best available rates and exclusive offers.</p>`;
        body += `<p style="font-size: 16px; color: #555;">We look forward to working with you and providing exceptional service.</p>`;
        body += `</div>`;
        
        // Footer
        body += `<div style="text-align: center; margin-top: 40px; padding-top: 20px; border-top: 2px solid #bdc3c7; color: #7f8c8d; font-size: 14px;">`;
        body += `<p style="margin: 5px 0;">Best regards,</p>`;
        body += `<p style="margin: 5px 0; font-weight: bold;">Hotel Management Team</p>`;
        body += `<p style="margin: 5px 0;">For inquiries: ${email || phone || 'Contact us'}</p>`;
        body += `</div>`;
        
        body += `</body></html>`;

        const encodedSubject = encodeURIComponent(subject);
        const encodedBody = encodeURIComponent(body);

        window.open(
            `https://mail.google.com/mail/?view=cm&fs=1&su=${encodedSubject}&body=${encodedBody}`,
            "_blank"
        );
    };

    // Preview Function - Modal
    const handlePreview = () => {
        setShowPreviewModal(true);
    };

    // Close Preview Modal
    const closePreviewModal = () => {
        setShowPreviewModal(false);
    };

    // Filter functions
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
        setCurrentPage(1);
    };

    // Apply filters
    const applyFilters = () => {
        setShowFilters(false);
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            minStars: 0,
            maxStars: 5,
            accommodationType: "",
            country: ""
        });
        setSortBy("relevance");
        setSortOrder("asc");
    };

    // Toggle sort order
    const toggleSortOrder = () => {
        setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    };

    // Filter and sort results
    const filteredAndSortedResults = useCallback(() => {
        let filtered = [...searchResults];

        // Apply star rating filter
        filtered = filtered.filter(hotel => {
            const hotelStars = hotel.starCategory || 0;
            return hotelStars >= filters.minStars && hotelStars <= filters.maxStars;
        });

        // Apply accommodation type filter
        if (filters.accommodationType) {
            filtered = filtered.filter(hotel => 
                hotel.accommdationType?.toLowerCase().includes(filters.accommodationType.toLowerCase())
            );
        }

        // Apply country filter
        if (filters.country) {
            filtered = filtered.filter(hotel => 
                hotel.country?.toLowerCase().includes(filters.country.toLowerCase())
            );
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let aValue, bValue;

            switch (sortBy) {
                case "name":
                    aValue = a.hotelName || "";
                    bValue = b.hotelName || "";
                    break;
                case "stars":
                    aValue = a.starCategory || 0;
                    bValue = b.starCategory || 0;
                    break;
                case "city":
                    aValue = a.city || "";
                    bValue = b.city || "";
                    break;
                case "country":
                    aValue = a.country || "";
                    bValue = b.country || "";
                    break;
                default: // relevance
                    return 0;
            }

            if (sortOrder === "asc") {
                return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
            } else {
                return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
            }
        });

        return filtered;
    }, [searchResults, filters, sortBy, sortOrder]);

    // Room Details Functions - UPDATED: Removed validFrom and validTo from individual room
    const handleAddRoom = () => {
        if (newRoom.roomType && newRoom.mealPlan && newRoom.price) {
            // Convert room type to uppercase
            const uppercaseRoomType = newRoom.roomType.toUpperCase().trim();
            
            setRoomDetails([...roomDetails, { 
                ...newRoom, 
                roomType: uppercaseRoomType,
                id: Date.now()
                // validFrom and validTo removed from individual room
            }]);
            setNewRoom({
                roomType: "",
                mealPlan: "CPAI",
                extraBedPrice: "",
                childWithoutBedPrice: "",
                price: "",
                currency: "USD"
                // validFrom and validTo removed from newRoom state
            });
            setRoomTypeSearchTerm("");
            setShowAddRoomForm(false);
        }
    };

    const handleDeleteRoom = (roomId) => {
        setRoomDetails(roomDetails.filter(room => room.id !== roomId));
    };

    const handleEditRoom = (room) => {
        setNewRoom({
            roomType: room.roomType,
            mealPlan: room.mealPlan,
            extraBedPrice: room.extraBedPrice,
            childWithoutBedPrice: room.childWithoutBedPrice,
            price: room.price,
            currency: room.currency
        });
        setRoomTypeSearchTerm(room.roomType);
        setShowAddRoomForm(true);
        handleDeleteRoom(room.id);
    };

    const handleNewRoomChange = (e) => {
        const { name, value, type } = e.target;
        // For room type field, convert to uppercase on change
        if (name === 'roomType') {
            const uppercaseValue = value.toUpperCase();
            setNewRoom(prev => ({
                ...prev,
                [name]: uppercaseValue
            }));
            // Also update search term for dropdown
            handleRoomTypeSearch(uppercaseValue);
        } else {
            setNewRoom(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // NEW: Handle global validity date changes
    const handleGlobalValidFromChange = (e) => {
        setGlobalValidFrom(e.target.value);
    };

    const handleGlobalValidToChange = (e) => {
        setGlobalValidTo(e.target.value);
    };

    // Auto-complete handler for room type - Search in uppercase
    const handleRoomTypeSearch = (searchTerm) => {
        const uppercaseTerm = searchTerm.toUpperCase();
        setRoomTypeSearchTerm(uppercaseTerm);
        setNewRoom(prev => ({ ...prev, roomType: uppercaseTerm }));
        
        if (uppercaseTerm.trim().length > 0) {
            const filtered = roomTypeOptions.filter(type =>
                type.includes(uppercaseTerm)
            );
            setRoomTypeSuggestions(filtered);
            setShowRoomTypeDropdown(true);
        } else {
            setRoomTypeSuggestions([]);
            setShowRoomTypeDropdown(false);
        }
    };

    // Select room type from suggestions
    const selectRoomType = (roomType) => {
        setNewRoom(prev => ({ ...prev, roomType }));
        setRoomTypeSearchTerm("");
        setRoomTypeSuggestions([]);
        setShowRoomTypeDropdown(false);
    };

    // Amenities Functions - Points system
    const handleAddAmenityPoint = () => {
        if (newAmenityPoint.trim()) {
            setAmenities([...amenities, { 
                id: Date.now(), 
                point: newAmenityPoint.trim() 
            }]);
            setNewAmenityPoint("");
        }
    };

    const handleDeleteAmenityPoint = (pointId) => {
        setAmenities(amenities.filter(point => point.id !== pointId));
    };

    const handleEditAmenityPoint = (point) => {
        setNewAmenityPoint(point.point);
        handleDeleteAmenityPoint(point.id);
    };

    // Terms & Conditions Functions - Points system
    const handleAddTermPoint = () => {
        if (newTermPoint.trim()) {
            setTermsAndConditions([...termsAndConditions, { 
                id: Date.now(), 
                point: newTermPoint.trim() 
            }]);
            setNewTermPoint("");
        }
    };

    const handleDeleteTermPoint = (pointId) => {
        setTermsAndConditions(termsAndConditions.filter(point => point.id !== pointId));
    };

    const handleEditTermPoint = (point) => {
        setNewTermPoint(point.point);
        handleDeleteTermPoint(point.id);
    };

    // Cancellation Policy Functions - Points system
    const handleAddCancellationPoint = () => {
        if (newCancellationPoint.trim()) {
            setCancellationPolicy([...cancellationPolicy, { 
                id: Date.now(), 
                point: newCancellationPoint.trim() 
            }]);
            setNewCancellationPoint("");
        }
    };

    const handleDeleteCancellationPoint = (pointId) => {
        setCancellationPolicy(cancellationPolicy.filter(point => point.id !== pointId));
    };

    const handleEditCancellationPoint = (point) => {
        setNewCancellationPoint(point.point);
        handleDeleteCancellationPoint(point.id);
    };

    // Child Policy Functions - Points system
    const handleAddChildPolicyPoint = () => {
        if (newChildPolicyPoint.trim()) {
            setChildPolicy([...childPolicy, { 
                id: Date.now(), 
                point: newChildPolicyPoint.trim() 
            }]);
            setNewChildPolicyPoint("");
        }
    };

    const handleDeleteChildPolicyPoint = (pointId) => {
        setChildPolicy(childPolicy.filter(point => point.id !== pointId));
    };

    const handleEditChildPolicyPoint = (point) => {
        setNewChildPolicyPoint(point.point);
        handleDeleteChildPolicyPoint(point.id);
    };

    // Bank Details Functions
    const handleAddBankDetail = () => {
        if (newBankDetail.bankName && newBankDetail.accountNumber) {
            setBankDetails([...bankDetails, { ...newBankDetail, id: Date.now() }]);
            setNewBankDetail({
                bankName: "",
                accountNumber: "",
                swiftIfscCode: ""
            });
            setShowAddBankForm(false);
        }
    };

    const handleDeleteBankDetail = (bankId) => {
        setBankDetails(bankDetails.filter(bank => bank.id !== bankId));
    };

    const handleEditBankDetail = (bank) => {
        setNewBankDetail(bank);
        setShowAddBankForm(true);
        handleDeleteBankDetail(bank.id);
    };

    const handleBankDetailChange = (e) => {
        const { name, value } = e.target;
        setNewBankDetail(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Contact Details Functions
    const handleAddContact = () => {
        if (newContact.contactName || newContact.email || newContact.phone || newContact.whatsapp) {
            setSelectedHotel(prev => ({
                ...prev,
                contactName: newContact.contactName || prev.contactName,
                email: newContact.email || prev.email,
                phone: newContact.phone || prev.phone,
                whatsapp: newContact.whatsapp || prev.whatsapp
            }));
            setNewContact({
                contactName: "",
                email: "",
                phone: "",
                whatsapp: ""
            });
            setShowAddContactForm(false);
        }
    };

    const handleContactChange = (e) => {
        const { name, value } = e.target;
        setNewContact(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Get currency symbol
    const getCurrencySymbol = (currencyCode) => {
        const currency = currencyOptions.find(c => c.code === currencyCode);
        return currency ? currency.symbol : currencyCode;
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString || dateString === "Not specified") return "Not specified";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Render star rating - FIXED ICONS
    const renderStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;

        for (let i = 1; i <= 5; i++) {
            if (i <= fullStars) {
                stars.push(<i key={i} className="bi bi-star-fill text-warning"></i>);
            } else if (i === fullStars + 1 && hasHalfStar) {
                stars.push(<i key={i} className="bi bi-star-half text-warning"></i>);
            } else {
                stars.push(<i key={i} className="bi bi-star text-warning"></i>);
            }
        }
        return <span className="d-inline-flex align-items-center">{stars}</span>;
    };

    // Tab navigation
    const tabs = [
        { id: "roomDetails", label: "Room Details", icon: "bi-bed" },
        { id: "amenities", label: "Amenities", icon: "bi-wifi" },
        { id: "terms", label: "Terms & Conditions", icon: "bi-file-text" },
        { id: "cancellation", label: "Cancellation Policy", icon: "bi-calendar-event" },
        { id: "childPolicy", label: "Child Policy", icon: "bi-person" },
        { id: "bankDetails", label: "Bank Details", icon: "bi-bank" }
    ];

    // FIXED: Render room type field with auto-complete - improved dropdown positioning
    const renderRoomTypeField = () => (
        <div className="col-md-3">
            <label className="form-label">Room Type *</label>
            <div className="position-relative" ref={roomTypeInputRef}>
                <input
                    type="text"
                    className="form-control text-uppercase"
                    value={roomTypeSearchTerm || newRoom.roomType}
                    onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        handleRoomTypeSearch(value);
                    }}
                    placeholder="SEARCH ROOM TYPE..."
                    required
                    onFocus={() => {
                        if (roomTypeSearchTerm.length > 0 || newRoom.roomType.length > 0) {
                            handleRoomTypeSearch(roomTypeSearchTerm || newRoom.roomType);
                        }
                    }}
                    style={{ textTransform: 'uppercase' }}
                />
                {showRoomTypeDropdown && roomTypeSuggestions.length > 0 && (
                    <div className="position-absolute w-100 bg-white border rounded shadow-lg room-type-dropdown"
                         style={{ 
                             zIndex: 1050, 
                             maxHeight: '200px', 
                             overflowY: 'auto',
                             top: '100%',
                             left: 0,
                             marginTop: '2px'
                         }}>
                        {roomTypeSuggestions.map((type, index) => (
                            <div key={index}
                                 className="dropdown-item cursor-pointer"
                                 onClick={() => selectRoomType(type)}
                                 style={{ 
                                     padding: '8px 12px', 
                                     cursor: 'pointer',
                                     borderBottom: '1px solid #f0f0f0',
                                     textTransform: 'uppercase'
                                 }}>
                                {type}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    // Pagination calculations
    const filteredResults = filteredAndSortedResults();
    const indexOfLastResult = currentPage * resultsPerPage;
    const indexOfFirstResult = indexOfLastResult - resultsPerPage;
    const currentResults = filteredResults.slice(indexOfFirstResult, indexOfLastResult);
    const totalPages = Math.ceil(filteredResults.length / resultsPerPage);

    if (loading) return <p>Loading...</p>;

    if (!user) {
        navigate("/");
    }

    // Preview Modal Component - UPDATED: Show global validity dates
    const PreviewModal = () => {
        if (!showPreviewModal || !selectedHotel) return null;

        return (
            <div className="modal-overlay" style={styles.modalOverlay}>
                <div className="modal-container" style={styles.modalContainer}>
                    {/* Modal Header */}
                    <div className="modal-header" style={styles.modalHeader}>
                        <div className="d-flex align-items-center">
                            <i className="bi bi-eye me-2" style={{ fontSize: '1.5rem' }}></i>
                            <h5 className="mb-0" style={{ color: '#fff' }}>
                                Preview - {selectedHotel.hotelName}
                            </h5>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={closePreviewModal}
                            aria-label="Close"
                        ></button>
                    </div>

                    {/* Modal Body */}
                    <div className="modal-body" style={styles.modalBody}>
                        {/* Hotel Header Section */}
                        <div className="hotel-header" style={styles.hotelHeader}>
                            <div className="row align-items-center">
                                <div className="col-md-4">
                                    {selectedHotel.hotelImage ? (
                                        <img
                                            src={selectedHotel.hotelImage}
                                            alt={selectedHotel.hotelName}
                                            style={styles.hotelImage}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = `https://via.placeholder.com/400x250/667eea/ffffff?text=${encodeURIComponent(selectedHotel.hotelName?.substring(0, 30) || 'Hotel')}`;
                                            }}
                                        />
                                    ) : (
                                        <div style={styles.hotelImagePlaceholder}>
                                            <i className="bi bi-building" style={{ fontSize: '48px' }}></i>
                                            <div className="mt-2">Hotel Image</div>
                                        </div>
                                    )}
                                </div>
                                <div className="col-md-8">
                                    <h2 style={styles.hotelName}>{selectedHotel.hotelName}</h2>
                                    <div className="d-flex align-items-center mb-3">
                                        {renderStars(selectedHotel.starCategory || 0)}
                                        <span className="ms-2 fw-bold" style={{ color: '#ffc107' }}>
                                            {selectedHotel.starCategory || 'N/A'} Star
                                        </span>
                                        <span className="ms-3 badge bg-primary">
                                            {selectedHotel.accommdationType || 'Hotel'}
                                        </span>
                                    </div>
                                    <p style={styles.hotelDescription}>
                                        {selectedHotel.description || 'No description available.'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Contact & Property Details Section */}
                        <div className="row mb-4">
                            {/* Contact Details */}
                            <div className="col-md-6 mb-4">
                                <div className="card h-100" style={styles.detailCard}>
                                    <div className="card-header" style={styles.detailCardHeader}>
                                        <i className="bi bi-person me-2"></i>
                                        Contact Details
                                    </div>
                                    <div className="card-body">
                                        {selectedHotel.contactName && (
                                            <div className="mb-3">
                                                <small className="text-muted d-block">Contact Person</small>
                                                <strong>{selectedHotel.contactName}</strong>
                                            </div>
                                        )}
                                        {selectedHotel.email && (
                                            <div className="mb-3">
                                                <small className="text-muted d-block">Email</small>
                                                <strong>
                                                    <a href={`mailto:${selectedHotel.email}`} style={{ color: '#0d6efd' }}>
                                                        {selectedHotel.email}
                                                    </a>
                                                </strong>
                                            </div>
                                        )}
                                        {selectedHotel.phone && (
                                            <div className="mb-3">
                                                <small className="text-muted d-block">Phone</small>
                                                <strong>{selectedHotel.phone}</strong>
                                            </div>
                                        )}
                                        {selectedHotel.whatsapp && (
                                            <div className="mb-3">
                                                <small className="text-muted d-block">WhatsApp</small>
                                                <strong>{selectedHotel.whatsapp}</strong>
                                            </div>
                                        )}
                                        {!selectedHotel.contactName && !selectedHotel.email && !selectedHotel.phone && !selectedHotel.whatsapp && (
                                            <div className="text-muted text-center py-3">
                                                <i className="bi bi-info-circle me-1"></i>
                                                No contact details available
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Property Details */}
                            <div className="col-md-6 mb-4">
                                <div className="card h-100" style={styles.detailCard}>
                                    <div className="card-header" style={styles.detailCardHeader}>
                                        <i className="bi bi-geo-alt me-2"></i>
                                        Property Details
                                    </div>
                                    <div className="card-body">
                                        {selectedHotel.address && (
                                            <div className="mb-3">
                                                <small className="text-muted d-block">Address</small>
                                                <strong>{selectedHotel.address}</strong>
                                            </div>
                                        )}
                                        <div className="row mb-3">
                                            <div className="col-6">
                                                <small className="text-muted d-block">City</small>
                                                <strong>{selectedHotel.city || 'N/A'}</strong>
                                            </div>
                                            <div className="col-6">
                                                <small className="text-muted d-block">Country</small>
                                                <strong>{selectedHotel.country || 'N/A'}</strong>
                                            </div>
                                        </div>
                                        <div className="row mb-3">
                                            <div className="col-6">
                                                <small className="text-muted d-block">Property Type</small>
                                                <strong>{selectedHotel.accommdationType || 'N/A'}</strong>
                                            </div>
                                        </div>
                                        {selectedHotel.latitude && selectedHotel.longitude && (
                                            <div className="mb-3">
                                                <a
                                                    href={`https://www.google.com/maps?q=${selectedHotel.latitude},${selectedHotel.longitude}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-primary"
                                                >
                                                    <i className="bi bi-geo-alt me-1"></i>
                                                    View on Google Maps
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Room Details Section - UPDATED: Show global validity dates */}
                        {roomDetails.length > 0 && (
                            <div className="mb-4">
                                <h4 style={styles.sectionTitle}>
                                    <i className="bi bi-bed me-2"></i>
                                    Room Details
                                </h4>
                                
                                {/* Global Validity Dates Display */}
                                {globalValidFrom && globalValidTo && (
                                    <div className="alert alert-info mb-3">
                                        <i className="bi bi-calendar-check me-2"></i>
                                        <strong>Valid for all rooms:</strong> {formatDate(globalValidFrom)} to {formatDate(globalValidTo)}
                                    </div>
                                )}
                                
                                <div className="table-responsive">
                                    <table className="table table-hover" style={{ fontSize: '0.9rem' }}>
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th>Room Type</th>
                                                <th>Meal Plan</th>
                                                <th>Price</th>
                                                <th>Extra Bed</th>
                                                <th>Child w/o Bed</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {roomDetails.map((room) => {
                                                const currencySymbol = getCurrencySymbol(room.currency);
                                                const mealPlanLabel = mealPlanOptions.find(mp => mp.value === room.mealPlan)?.label || room.mealPlan;
                                                
                                                return (
                                                    <tr key={room.id}>
                                                        <td>
                                                            <strong style={{ textTransform: 'uppercase' }}>{room.roomType}</strong>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-info">
                                                                {mealPlanLabel}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span style={{ fontWeight: 'bold', color: '#28a745' }}>
                                                                {currencySymbol}{room.price}
                                                            </span>
                                                            <small className="text-muted ms-1">per night</small>
                                                        </td>
                                                        <td>
                                                            {room.extraBedPrice ? (
                                                                <span className="badge bg-success">
                                                                    {currencySymbol}{room.extraBedPrice}
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-secondary">-</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {room.childWithoutBedPrice ? (
                                                                <span className="badge bg-info">
                                                                    {currencySymbol}{room.childWithoutBedPrice}
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-secondary">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Additional Sections */}
                        {amenities.length > 0 && (
                            <div className="mb-4">
                                <h4 style={styles.sectionTitle}>
                                    <i className="bi bi-wifi me-2"></i>
                                    Amenities
                                </h4>
                                <div className="row">
                                    {amenities.map((amenity, index) => (
                                        <div key={amenity.id} className="col-md-4 mb-2">
                                            <div className="d-flex align-items-center">
                                                <i className="bi bi-check-circle-fill text-success me-2"></i>
                                                <span>{amenity.point}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Policies Section */}
                        {(termsAndConditions.length > 0 || cancellationPolicy.length > 0 || childPolicy.length > 0) && (
                            <div className="mb-4">
                                <h4 style={styles.sectionTitle}>
                                    <i className="bi bi-file-earmark-text me-2"></i>
                                    Policies
                                </h4>
                                
                                {termsAndConditions.length > 0 && (
                                    <div className="mb-4">
                                        <h5 className="text-primary mb-3">
                                            <i className="bi bi-file-text me-2"></i>
                                            Terms & Conditions
                                        </h5>
                                        <div className="row">
                                            {termsAndConditions.map((point) => (
                                                <div key={point.id} className="col-12 mb-2">
                                                    <div className="d-flex">
                                                        <i className="bi bi-dot me-2 text-primary"></i>
                                                        <span>{point.point}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {cancellationPolicy.length > 0 && (
                                    <div className="mb-4">
                                        <h5 className="text-danger mb-3">
                                            <i className="bi bi-calendar-x me-2"></i>
                                            Cancellation Policy
                                        </h5>
                                        <div className="row">
                                            {cancellationPolicy.map((point) => (
                                                <div key={point.id} className="col-12 mb-2">
                                                    <div className="d-flex">
                                                        <i className="bi bi-dot me-2 text-danger"></i>
                                                        <span>{point.point}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {childPolicy.length > 0 && (
                                    <div>
                                        <h5 className="text-success mb-3">
                                            <i className="bi bi-person-plus me-2"></i>
                                            Child Policy
                                        </h5>
                                        <div className="row">
                                            {childPolicy.map((point) => (
                                                <div key={point.id} className="col-12 mb-2">
                                                    <div className="d-flex">
                                                        <i className="bi bi-dot me-2 text-success"></i>
                                                        <span>{point.point}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Bank Details Section */}
                        {bankDetails.length > 0 && (
                            <div className="mb-4">
                                <h4 style={styles.sectionTitle}>
                                    <i className="bi bi-bank me-2"></i>
                                    Bank Details
                                </h4>
                                <div className="row">
                                    {bankDetails.map((bank) => (
                                        <div key={bank.id} className="col-md-6 mb-3">
                                            <div className="card" style={styles.bankCard}>
                                                <div className="card-body">
                                                    <h6 className="text-primary mb-3">{bank.bankName}</h6>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Account Number</small>
                                                        <strong>{bank.accountNumber}</strong>
                                                    </div>
                                                    {bank.swiftIfscCode && (
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">SWIFT/IFSC Code</small>
                                                            <strong>{bank.swiftIfscCode}</strong>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Modal Footer */}
                    <div className="modal-footer" style={styles.modalFooter}>
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={shareViaWhatsApp}
                        >
                            <i className="bi bi-whatsapp me-1"></i>
                            Share via WhatsApp
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={shareViaEmail}
                        >
                            <i className="bi bi-envelope me-1"></i>
                            Share via Email
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={closePreviewModal}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // CSS Styles for the Modal
    const styles = {
        modalOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
        },
        modalContainer: {
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        },
        modalHeader: {
            backgroundColor: '#667eea',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '20px',
            borderBottom: '1px solid #dee2e6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        modalBody: {
            padding: '30px',
            overflowY: 'auto',
            maxHeight: 'calc(90vh - 140px)',
        },
        modalFooter: {
            padding: '20px',
            borderTop: '1px solid #dee2e6',
            backgroundColor: '#f8f9fa',
        },
        hotelHeader: {
            backgroundColor: '#f8f9fa',
            padding: '30px',
            borderRadius: '10px',
            marginBottom: '30px',
            border: '1px solid #e9ecef',
        },
        hotelImage: {
            width: '100%',
            height: '250px',
            objectFit: 'cover',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
        hotelImagePlaceholder: {
            width: '100%',
            height: '250px',
            backgroundColor: '#667eea',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            flexDirection: 'column',
        },
        hotelName: {
            color: '#2c3e50',
            marginBottom: '15px',
            fontWeight: '700',
        },
        hotelDescription: {
            color: '#6c757d',
            lineHeight: '1.6',
            fontSize: '1rem',
        },
        detailCard: {
            border: '1px solid #e9ecef',
            borderRadius: '10px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            transition: 'all 0.3s ease',
            height: '100%',
        },
        detailCardHeader: {
            backgroundColor: '#f8f9fa',
            color: '#495057',
            fontWeight: '600',
            borderBottom: '1px solid #e9ecef',
            padding: '15px 20px',
        },
        sectionTitle: {
            color: '#2c3e50',
            borderBottom: '2px solid #667eea',
            paddingBottom: '10px',
            marginBottom: '20px',
            fontWeight: '600',
        },
        roomCard: {
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
            height: '100%',
        },
        roomType: {
            color: '#2c3e50',
            fontWeight: '600',
            marginBottom: '10px',
            fontSize: '1rem',
        },
        price: {
            fontSize: '1.2rem',
            fontWeight: '700',
            color: '#28a745',
        },
        bankCard: {
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
        }
    };

    return (
        <div id="layout-wrapper">
            <Header User={user} />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="card">
                            <div className="p-2">
                                {/* <HotelDmcTabs pageName="MYHOTELS" /> */}
                            </div>
                            <div className="card-body">
                                <div className="row mb-4">
                                    <div className="col-12">
                                        <div className="d-flex align-items-center mb-4">
                                            <div className="flex-grow-1">
                                                <h5 className="card-title text-primary mb-0">
                                                    <i className="bi bi-building me-2"></i>
                                                    Search Your Hotels
                                                </h5>
                                            </div>
                                        </div>
                                        
                                        {/* Search Section with Auto-complete - FIXED DROPDOWN ISSUE */}
                                        <div className="card border-primary mb-4">
                                            <div className="card-body">
                                                <form onSubmit={handleSearchSubmit}>
                                                    <div className="row">
                                                        <div className="col-md-10">
                                                            <div className="position-relative">
                                                                <div className="input-group">
                                                                    <span className="input-group-text bg-light">
                                                                        <i className="bi bi-search"></i>
                                                                    </span>
                                                                    <input
                                                                        ref={searchInputRef}
                                                                        type="text"
                                                                        className="form-control form-control-lg"
                                                                        placeholder="Type hotel name (e.g., Taj, Marriott, Hilton)..."
                                                                        value={searchTerm}
                                                                        onChange={handleSearchChange}
                                                                        required
                                                                        minLength={2}
                                                                        autoComplete="off"
                                                                        onFocus={() => {
                                                                            if (searchTerm.trim().length >= 2 && searchSuggestions.length > 0) {
                                                                                setShowSearchDropdown(true);
                                                                            }
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="submit"
                                                                        className="btn btn-primary"
                                                                        disabled={isSearching}
                                                                    >
                                                                        {isSearching ? (
                                                                            <>
                                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                                Searching...
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <i className="bi bi-search me-2"></i>
                                                                                Search All
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                    <div className="mx-2"></div>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-danger"
                                                                        onClick={clearSearch}
                                                                    >
                                                                        <i className="bi bi-x-lg me-1"></i>
                                                                        Clear
                                                                    </button>
                                                                </div>
                                                                
                                                                {/* Search Suggestions Dropdown - FIXED: positioned relative to input */}
                                                                {showSearchDropdown && searchSuggestions.length > 0 && (
                                                                    <div className="position-absolute w-100 bg-white border rounded shadow-lg"
                                                                         ref={searchDropdownRef}
                                                                         style={{ 
                                                                             zIndex: 1050, 
                                                                             maxHeight: '300px', 
                                                                             overflowY: 'auto',
                                                                             top: '100%',
                                                                             left: 0,
                                                                             marginTop: '2px',
                                                                             position: 'absolute'
                                                                         }}>
                                                                        <div className="p-2 border-bottom bg-light">
                                                                            <small className="text-muted">
                                                                                <i className="bi bi-info-circle me-1"></i>
                                                                                Select a hotel:
                                                                            </small>
                                                                        </div>
                                                                        {searchSuggestions.map((hotel, index) => (
                                                                            <div key={hotel.id || hotel.hotelId || index}
                                                                                 className="dropdown-item cursor-pointer py-2 px-3"
                                                                                 onClick={() => selectSearchSuggestion(hotel)}
                                                                                 style={{ 
                                                                                     cursor: 'pointer',
                                                                                     borderBottom: '1px solid #f0f0f0',
                                                                                     transition: 'background-color 0.2s'
                                                                                 }}
                                                                                 onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                                                                 onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                                                                                <div className="d-flex justify-content-between align-items-center">
                                                                                    <div>
                                                                                        <strong>{hotel.hotelName}</strong>
                                                                                        <div className="text-muted small mt-1">
                                                                                            <i className="bi bi-geo-alt me-1"></i>
                                                                                            {hotel.city || ''} {hotel.country ? `, ${hotel.country}` : ''}
                                                                                            {hotel.starCategory && (
                                                                                                <span className="ms-2">
                                                                                                    {renderStars(hotel.starCategory)}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                    <i className="bi bi-chevron-right text-muted"></i>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <small className="text-muted d-block mt-2">
                                                                Start typing to see hotel suggestions. Click on a hotel name to select it, or click "Search All" to see all matching hotels.
                                                            </small>
                                                        </div>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                        
                                        {/* Save Button Section - NEW */}
                                        {showHotelDetails && selectedHotel && (
                                            <div className="card border-success mb-4">
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <h6 className="mb-1 text-success">
                                                                <i className="bi bi-cloud-upload me-2"></i>
                                                                Save Hotel Data
                                                            </h6>
                                                            <p className="text-muted small mb-0">
                                                                Save all room details, amenities, policies and bank information for this hotel
                                                            </p>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-3">
                                                            {saveSuccess && (
                                                                <span className="text-success">
                                                                    <i className="bi bi-check-circle-fill me-1"></i>
                                                                    Saved successfully!
                                                                </span>
                                                            )}
                                                            {saveError && (
                                                                <span className="text-danger">
                                                                    <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                                                    {saveError}
                                                                </span>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="btn btn-success"
                                                                onClick={saveHotelData}
                                                                disabled={isSaving}
                                                            >
                                                                {isSaving ? (
                                                                    <>
                                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                        Saving...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="bi bi-cloud-upload me-2"></i>
                                                                        Save Hotel
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Results Section - Shows when hotel is selected or Search All is clicked */}
                                        {showResults && (
                                            <div ref={resultsRef}>
                                                <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div>
                                                            <h2 className="text-muted">Your properties list</h2>
                                                            <h5 className="mb-0">
                                                                {totalResults} Hotel{totalResults !== 1 ? 's' : ''} Found
                                                                {searchTerm && ` for "${searchTerm}"`}
                                                            </h5>
                                                            <small className="text-muted">
                                                                {searchResults.length === 1 ? 
                                                                    'Showing selected hotel' : 
                                                                    'Showing all matching hotels'}
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {/* Results Grid */}
                                                {filteredResults.length > 0 ? (
                                                    <>
                                                        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-4 g-4 mb-4">
                                                            {currentResults.map((hotel, index) => (
                                                                <div key={hotel.id || hotel.hotelId || index} className="col">
                                                                    <div 
                                                                        className={`card h-100 hotel-card ${selectedHotel?.id === hotel.id || selectedHotel?.hotelId === hotel.hotelId ? 'border-primary border-2' : 'border-light'}`}
                                                                        onClick={() => handleHotelSelect(hotel)}
                                                                        style={{ 
                                                                            cursor: 'pointer',
                                                                            transition: 'all 0.3s ease',
                                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.currentTarget.style.transform = 'translateY(-5px)';
                                                                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                                                        }}
                                                                    >
                                                                        <div className="position-relative">
                                                                            {hotel.hotelImage ? (
                                                                                <img
                                                                                    src={hotel.hotelImage}
                                                                                    className="card-img-top"
                                                                                    alt={hotel.hotelName}
                                                                                    style={{ height: '180px', objectFit: 'cover', width: '100%' }}
                                                                                    onError={(e) => {
                                                                                        e.target.onerror = null;
                                                                                        e.target.src = `https://via.placeholder.com/300x180/cccccc/666666?text=${encodeURIComponent(hotel.hotelName?.substring(0, 20) || 'Hotel')}`;
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <div className="card-img-top bg-gradient-primary d-flex align-items-center justify-content-center"
                                                                                     style={{ height: '180px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                                                                    <i className="bi bi-building text-white" style={{ fontSize: '48px' }}></i>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        
                                                                        <div className="card-body">
                                                                            {/* Hotel Name with Star Rating */}
                                                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                                                <h6 className="card-title text-truncate mb-0 me-2">
                                                                                    {hotel.hotelName}
                                                                                </h6>
                                                                                <div className="d-flex align-items-center flex-shrink-0">
                                                                                    {renderStars(hotel.starCategory || 0)}
                                                                                    <span className="ms-1 fw-bold text-warning">
                                                                                        {hotel.starCategory || 'N/A'}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                            
                                                                            {/* Location */}
                                                                            <div className="d-flex align-items-center text-muted small mb-2">
                                                                                <i className="bi bi-geo-alt me-1 flex-shrink-0"></i>
                                                                                <span className="text-truncate">
                                                                                    {[hotel.city, hotel.country].filter(Boolean).join(', ')}
                                                                                </span>
                                                                            </div>
                                                                            
                                                                            {/* Description */}
                                                                            <div className="mb-2">
                                                                                <p className="text-muted small mb-0" style={{ fontSize: '0.85rem' }}>
                                                                                    {hotel.description || 'No description available. Click to view details.'}
                                                                                </p>
                                                                            </div>
                                                                            
                                                                            {/* Property Type Badge */}
                                                                            <div className="mb-2">
                                                                                <span className="badge bg-primary bg-opacity-10 text-primary me-1">
                                                                                    {hotel.accommdationType || 'Hotel'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        <div className="card-footer bg-transparent border-top pt-2 pb-2">
                                                                            <div className="d-flex justify-content-between align-items-center">
                                                                                <small className="text-muted">
                                                                                    {hotel.accommdationType || 'Hotel'}
                                                                                </small>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleHotelSelect(hotel);
                                                                                    }}
                                                                                >
                                                                                    View Details
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        
                                                        {/* Pagination - Only show if more than 1 hotel */}
                                                        {totalPages > 1 && (
                                                            <nav aria-label="Hotel results pagination" className="mt-4">
                                                                <ul className="pagination justify-content-center flex-wrap">
                                                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                        <button
                                                                            className="page-link"
                                                                            onClick={() => setCurrentPage(1)}
                                                                            disabled={currentPage === 1}
                                                                        >
                                                                            First
                                                                        </button>
                                                                    </li>
                                                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                        <button
                                                                            className="page-link"
                                                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                                            disabled={currentPage === 1}
                                                                        >
                                                                            Previous
                                                                        </button>
                                                                    </li>
                                                                    
                                                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                                        let pageNum;
                                                                        if (totalPages <= 5) {
                                                                            pageNum = i + 1;
                                                                        } else if (currentPage <= 3) {
                                                                            pageNum = i + 1;
                                                                        } else if (currentPage >= totalPages - 2) {
                                                                            pageNum = totalPages - 4 + i;
                                                                        } else {
                                                                            pageNum = currentPage - 2 + i;
                                                                        }
                                                                        
                                                                        return (
                                                                            <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                                                <button
                                                                                    className="page-link"
                                                                                    onClick={() => setCurrentPage(pageNum)}
                                                                                >
                                                                                    {pageNum}
                                                                                </button>
                                                                            </li>
                                                                        );
                                                                    })}
                                                                    
                                                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                        <button
                                                                            className="page-link"
                                                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                                            disabled={currentPage === totalPages}
                                                                        >
                                                                            Next
                                                                        </button>
                                                                    </li>
                                                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                        <button
                                                                            className="page-link"
                                                                            onClick={() => setCurrentPage(totalPages)}
                                                                            disabled={currentPage === totalPages}
                                                                        >
                                                                            Last
                                                                        </button>
                                                                    </li>
                                                                </ul>
                                                                <div className="text-center text-muted mt-2">
                                                                    Page {currentPage} of {totalPages} • Showing {currentResults.length} of {filteredResults.length} hotels
                                                                </div>
                                                            </nav>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="alert alert-warning text-center py-5">
                                                        <i className="bi bi-search mb-3 text-warning" style={{ fontSize: '48px' }}></i>
                                                        <h5>No hotels found matching your criteria</h5>
                                                        <p className="text-muted">
                                                            Try adjusting your search or filters
                                                        </p>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-primary me-2"
                                                            onClick={resetFilters}
                                                        >
                                                            Reset Filters
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary"
                                                            onClick={clearSearch}
                                                        >
                                                            Clear Search
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Hotel Details Section with Tabs - UPDATED: Added global validity date fields */}
                                        {showHotelDetails && selectedHotel && (
                                            <div id="hotel-details-section" className="card border-success mt-4">
                                                <div className="card-header bg-success bg-opacity-10">
                                                    {/* Row 1: Hotel Name with Stars */}
                                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                                        <div className="d-flex align-items-center">
                                                            <i className="bi bi-building me-2 text-success"></i>
                                                            <h5 className="mb-0 text-success">
                                                                {selectedHotel.hotelName}
                                                            </h5>
                                                            <div className="ms-3 d-flex align-items-center">
                                                                {renderStars(selectedHotel.starCategory || 0)}
                                                                <span className="ms-2 fw-bold text-warning">
                                                                    {selectedHotel.starCategory || 'N/A'} Star
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Row 2: Action Buttons with Preview and Save */}
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-info btn-sm"
                                                            onClick={handlePreview}
                                                            title="Preview Hotel Details"
                                                        >
                                                            <i className="bi bi-eye me-1"></i>
                                                            Preview
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-success btn-sm"
                                                            onClick={saveHotelData}
                                                            disabled={isSaving}
                                                            title="Save Hotel Data"
                                                        >
                                                            <i className="bi bi-cloud-upload me-1"></i>
                                                            {isSaving ? 'Saving...' : 'Save'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-success btn-sm"
                                                            onClick={shareViaWhatsApp}
                                                            title="Share via WhatsApp Web"
                                                        >
                                                            <i className="bi bi-whatsapp me-1"></i>
                                                            WhatsApp
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary btn-sm"
                                                            onClick={shareViaEmail}
                                                            title="Share via Email"
                                                        >
                                                            <i className="bi bi-envelope me-1"></i>
                                                            Email
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger btn-sm"
                                                            onClick={closeHotelDetails}
                                                            title="Close"
                                                        >
                                                            <i className="bi bi-x-lg"></i>
                                                            Close
                                                        </button>
                                                    </div>
                                                </div>
                                                
                                                <div className="card-body">
                                                    {/* Basic Hotel Info Section */}
                                                    <div className="row mb-4">
                                                        <div className="col-md-4 mb-4 mb-md-0">
                                                            {/* Hotel Image */}
                                                            <div className="mb-3">
                                                                {selectedHotel.hotelImage ? (
                                                                    <img
                                                                        src={selectedHotel.hotelImage}
                                                                        className="img-fluid rounded shadow"
                                                                        alt={selectedHotel.hotelName}
                                                                        style={{ 
                                                                            width: '100%', 
                                                                            height: '250px',
                                                                            objectFit: 'cover'
                                                                        }}
                                                                        onError={(e) => {
                                                                            e.target.onerror = null;
                                                                            e.target.src = `https://via.placeholder.com/400x250/cccccc/666666?text=${encodeURIComponent(selectedHotel.hotelName?.substring(0, 30) || 'Hotel Image')}`;
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div 
                                                                        className="bg-gradient-primary rounded shadow d-flex align-items-center justify-content-center"
                                                                        style={{ 
                                                                            height: '250px',
                                                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                                                        }}
                                                                    >
                                                                        <i className="bi bi-building text-white" style={{ fontSize: '64px' }}></i>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            
                                                            {/* Description */}
                                                            <div className="card border-info mb-3">
                                                                <div className="card-body">
                                                                    <h6 className="card-title text-info mb-2">
                                                                        <i className="bi bi-info-circle me-1"></i>
                                                                        Description
                                                                    </h6>
                                                                    <p className="mb-0 text-muted" style={{ 
                                                                        maxHeight: '120px',
                                                                        overflowY: 'auto',
                                                                        paddingRight: '5px'
                                                                    }}>
                                                                        {selectedHotel.description || 'No description available for this property.'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Contact and Location Details */}
                                                        <div className="col-md-8">
                                                            <div className="row">
                                                                {/* Contact Details Card */}
                                                                <div className="col-lg-6 mb-4">
                                                                    <div className="card h-100 border">
                                                                        <div className="card-header bg-light">
                                                                            <div className="d-flex align-items-center mb-2">
                                                                                <h6 className="mb-0 text-muted">
                                                                                    <i className="bi bi-person me-2"></i>
                                                                                    Contact Details
                                                                                </h6>
                                                                            </div>
                                                                            <div className="d-flex justify-content-end">
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                    onClick={() => setShowAddContactForm(!showAddContactForm)}
                                                                                    title="Update Contact Details"
                                                                                >
                                                                                    <i className="bi bi-pencil"></i>
                                                                                    Edit
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        <div className="card-body">
                                                                            <div className="mb-4">
                                                                                <div className="mb-2">
                                                                                    <strong>Contact Name:</strong>
                                                                                    <span className="mb-0 ms-2 text-dark">
                                                                                        {selectedHotel.contactName || user?.name || 'Not Available'}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="mb-2">
                                                                                    <strong>Email:</strong>
                                                                                    <span className="mb-0 ms-2 text-dark">
                                                                                        {selectedHotel.email || user?.email || 'Not Available'}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="mb-2">
                                                                                    <strong>Phone:</strong>
                                                                                    <span className="mb-0 ms-2 text-dark">
                                                                                        {selectedHotel.phone || user?.phoneNumber || 'Not Available'}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="mb-3">
                                                                                    <strong>WhatsApp:</strong>
                                                                                    <span className="mb-0 ms-2 text-dark">
                                                                                        {selectedHotel.whatsapp || user?.whatsAppNumber || 'Not Available'}
                                                                                    </span>
                                                                                </div>
                                                                                
                                                                                {/* Add Contact Form */}
                                                                                {showAddContactForm && (
                                                                                    <div className="card border-info p-3 mb-3">
                                                                                        <h6 className="text-info mb-3">
                                                                                            <i className="bi bi-pencil me-1"></i>
                                                                                            Update Contact Details
                                                                                        </h6>
                                                                                        <div className="row g-2">
                                                                                            <div className="col-12">
                                                                                                <label className="form-label">Contact Name</label>
                                                                                                <input
                                                                                                    type="text"
                                                                                                    className="form-control form-control-sm"
                                                                                                    name="contactName"
                                                                                                    value={newContact.contactName}
                                                                                                    onChange={handleContactChange}
                                                                                                    placeholder={selectedHotel.contactName || user?.name || "Enter contact name"}
                                                                                                />
                                                                                            </div>
                                                                                            <div className="col-md-6">
                                                                                                <label className="form-label">Email</label>
                                                                                                <input
                                                                                                    type="email"
                                                                                                    className="form-control form-control-sm"
                                                                                                    name="email"
                                                                                                    value={newContact.email}
                                                                                                    onChange={handleContactChange}
                                                                                                    placeholder={selectedHotel.email || user?.email || "Enter email"}
                                                                                                />
                                                                                            </div>
                                                                                            <div className="col-md-6">
                                                                                                <label className="form-label">Phone</label>
                                                                                                <input
                                                                                                    type="tel"
                                                                                                    className="form-control form-control-sm"
                                                                                                    name="phone"
                                                                                                    value={newContact.phone}
                                                                                                    onChange={handleContactChange}
                                                                                                    placeholder={selectedHotel.phone || user?.phoneNumber || "Enter phone number"}
                                                                                                />
                                                                                            </div>
                                                                                            <div className="col-12">
                                                                                                <label className="form-label">WhatsApp</label>
                                                                                                <input
                                                                                                    type="tel"
                                                                                                    className="form-control form-control-sm"
                                                                                                    name="whatsapp"
                                                                                                    value={newContact.whatsapp}
                                                                                                    onChange={handleContactChange}
                                                                                                    placeholder={selectedHotel.whatsapp || user?.whatsAppNumber || "Enter WhatsApp number"}
                                                                                                />
                                                                                            </div>
                                                                                            <div className="col-12">
                                                                                                <div className="d-flex gap-2 mt-2">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-sm btn-primary"
                                                                                                        onClick={handleAddContact}
                                                                                                    >
                                                                                                        <i className="bi bi-check me-1"></i>
                                                                                                        Update
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-sm btn-danger"
                                                                                                        onClick={() => setShowAddContactForm(false)}
                                                                                                    >
                                                                                                        Cancel
                                                                                                    </button>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Property Details Card */}
                                                                <div className="col-lg-6 mb-4">
                                                                    <div className="card h-100 border">
                                                                        <div className="card-header bg-light">
                                                                            <div className="d-flex justify-content-between align-items-center">
                                                                                <h6 className="mb-0 text-muted">
                                                                                    <i className="bi bi-info-circle me-2"></i>
                                                                                    Property Details
                                                                                </h6>
                                                                                <span className="badge bg-primary">
                                                                                    {selectedHotel.accommdationType || 'Hotel'}
                                                                                </span>
                                                                            </div>
                                                                        </div>

                                                                        <div className="card-body">
                                                                            {/* Location Details */}
                                                                            <div className="mb-3">
                                                                                <div className="mb-1">
                                                                                    <strong>Address:</strong>
                                                                                    <span className="ms-2 text-dark text-break">
                                                                                        {selectedHotel.address || 'Not Available'}
                                                                                    </span>
                                                                                </div>

                                                                                <div className="mb-1">
                                                                                    <strong>City:</strong>
                                                                                    <span className="ms-2 text-dark">
                                                                                        {selectedHotel.city || 'N/A'}
                                                                                    </span>
                                                                                </div>

                                                                                <div className="mb-1">
                                                                                    <strong>State:</strong>
                                                                                    <span className="ms-2 text-dark">
                                                                                        {selectedHotel.state || 'N/A'}
                                                                                    </span>
                                                                                </div>

                                                                                <div className="mb-1">
                                                                                    <strong>Country:</strong>
                                                                                    <span className="ms-2 text-dark">
                                                                                        {selectedHotel.country || 'N/A'}
                                                                                    </span>
                                                                                </div>

                                                                                <div className="mb-2">
                                                                                    <strong>Postal Code:</strong>
                                                                                    <span className="ms-2 text-dark">
                                                                                        {selectedHotel.postalCode || 'N/A'}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                            
                                                                            {/* Google Maps Link - Direct button without label */}
                                                                            {selectedHotel.latitude && selectedHotel.longitude && (
                                                                                <div className="mb-4">
                                                                                    <a
                                                                                        href={`https://www.google.com/maps?q=${selectedHotel.latitude},${selectedHotel.longitude}`}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="btn btn-sm btn-primary"
                                                                                    >
                                                                                        <i className="bi bi-geo-alt me-1"></i>
                                                                                        View on Google Maps
                                                                                    </a>
                                                                                </div>
                                                                            )}

                                                                            {/* Check-in / Check-out */}
                                                                            <div>
                                                                                <h6 className="text-primary mb-2">
                                                                                    <i className="bi bi-calendar me-1"></i>
                                                                                    Check-in & Check-out Times
                                                                                </h6>
                                                                                <div className="row">
                                                                                    <div className="col-md-6 mb-2">
                                                                                        <div className="card border-success">
                                                                                            <div className="card-body text-center p-2">
                                                                                                <small className="text-success">Check-in</small>
                                                                                                <h6 className="mb-0">
                                                                                                    {selectedHotel.checkIn || 'Not Specified'}
                                                                                                </h6>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="col-md-6 mb-2">
                                                                                        <div className="card border-info">
                                                                                            <div className="card-body text-center p-2">
                                                                                                <small className="text-info">Check-out</small>
                                                                                                <h6 className="mb-0">
                                                                                                    {selectedHotel.checkout || 'Not Specified'}
                                                                                                </h6>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Global Validity Dates Section - NEW */}
                                                    <div className="row mb-4">
                                                        <div className="col-12">
                                                            <div className="card border-info">
                                                                <div className="card-header bg-info bg-opacity-10">
                                                                    <h6 className="mb-0 text-info">
                                                                        <i className="bi bi-calendar-range me-2"></i>
                                                                        Validity Dates (Applies to all rooms)
                                                                    </h6>
                                                                </div>
                                                                <div className="card-body">
                                                                    <div className="row align-items-end">
                                                                        <div className="col-md-5">
                                                                            <label className="form-label">Valid From</label>
                                                                            <input
                                                                                type="date"
                                                                                className="form-control"
                                                                                value={globalValidFrom}
                                                                                onChange={handleGlobalValidFromChange}
                                                                            />
                                                                        </div>
                                                                        <div className="col-md-5">
                                                                            <label className="form-label">Valid To</label>
                                                                            <input
                                                                                type="date"
                                                                                className="form-control"
                                                                                value={globalValidTo}
                                                                                onChange={handleGlobalValidToChange}
                                                                            />
                                                                        </div>
                                                                        <div className="col-md-2">
                                                                            {globalValidFrom && globalValidTo && (
                                                                                <div className="text-success">
                                                                                    <i className="bi bi-check-circle-fill me-1"></i>
                                                                                    Set for all rooms
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <small className="text-muted d-block mt-2">
                                                                        These dates will be applied to all room types you add
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Tabs Navigation */}
                                                    <div className="row mb-4">
                                                        <div className="col-12">
                                                            <ul className="nav nav-tabs nav-tabs-custom" role="tablist">
                                                                {tabs.map((tab) => (
                                                                    <li key={tab.id} className="nav-item" role="presentation">
                                                                        <button
                                                                            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                                                                            onClick={() => setActiveTab(tab.id)}
                                                                            type="button"
                                                                        >
                                                                            <i className={`${tab.icon} me-1`}></i>
                                                                            {tab.label}
                                                                        </button>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Tab Content */}
                                                    <div className="tab-content">
                                                        {/* Room Details Tab - UPDATED: Removed date fields from add form and table */}
                                                        {activeTab === "roomDetails" && (
                                                            <div className="tab-pane fade show active">
                                                                <div className="card border-primary">
                                                                    <div className="card-header bg-primary bg-opacity-10 d-flex justify-content-between align-items-center">
                                                                        <h6 className="mb-0">
                                                                            <i className="bi bi-bed me-2"></i>
                                                                            Room Details - {selectedHotel.hotelName}
                                                                        </h6>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-primary"
                                                                            onClick={() => setShowAddRoomForm(!showAddRoomForm)}
                                                                        >
                                                                            <i className="bi bi-plus me-1"></i>
                                                                            Add Room
                                                                        </button>
                                                                    </div>
                                                                    
                                                                    <div className="card-body p-0">
                                                                        {/* Add Room Form - UPDATED: Removed date fields */}
                                                                        {showAddRoomForm && (
                                                                            <div className="card border-info m-3">
                                                                                <div className="card-body">
                                                                                    <h6 className="card-title text-info mb-3">Add New Room</h6>
                                                                                    <div className="row g-3 align-items-end">
                                                                                        {/* Auto-complete Room Type Field */}
                                                                                        {renderRoomTypeField()}
                                                                                        
                                                                                        <div className="col-md-3">
                                                                                            <label className="form-label">Meal Plan *</label>
                                                                                            <select
                                                                                                className="form-select"
                                                                                                name="mealPlan"
                                                                                                value={newRoom.mealPlan}
                                                                                                onChange={handleNewRoomChange}
                                                                                                required
                                                                                            >
                                                                                                {mealPlanOptions.map((option, index) => (
                                                                                                    <option key={index} value={option.value}>
                                                                                                        {option.label}
                                                                                                    </option>
                                                                                                ))}
                                                                                            </select>
                                                                                        </div>
                                                                                        <div className="col-md-3">
                                                                                            <label className="form-label">Price *</label>
                                                                                            <div className="input-group">
                                                                                                <input
                                                                                                    type="number"
                                                                                                    className="form-control"
                                                                                                    name="price"
                                                                                                    value={newRoom.price}
                                                                                                    onChange={handleNewRoomChange}
                                                                                                    placeholder="Price"
                                                                                                    required
                                                                                                    min="0"
                                                                                                />
                                                                                                <select
                                                                                                    className="form-select"
                                                                                                    name="currency"
                                                                                                    value={newRoom.currency}
                                                                                                    onChange={handleNewRoomChange}
                                                                                                    style={{ width: '80px' }}
                                                                                                >
                                                                                                    {currencyOptions.map((currency) => (
                                                                                                        <option key={currency.code} value={currency.code}>
                                                                                                            {currency.code}
                                                                                                        </option>
                                                                                                    ))}
                                                                                                </select>
                                                                                            </div>
                                                                                        </div>
                                                                                        
                                                                                        {/* Extra Bed Field - Always visible */}
                                                                                        <div className="col-md-3">
                                                                                            <label className="form-label">Extra Bed Price</label>
                                                                                            <div className="input-group">
                                                                                                <input
                                                                                                    type="number"
                                                                                                    className="form-control"
                                                                                                    name="extraBedPrice"
                                                                                                    value={newRoom.extraBedPrice}
                                                                                                    onChange={handleNewRoomChange}
                                                                                                    placeholder="Enter price"
                                                                                                    min="0"
                                                                                                />
                                                                                                <span className="input-group-text">
                                                                                                    {getCurrencySymbol(newRoom.currency)}
                                                                                                </span>
                                                                                            </div>
                                                                                        </div>
                                                                                        
                                                                                        {/* Child without Bed Field - Always visible */}
                                                                                        <div className="col-md-3">
                                                                                            <label className="form-label">Child without Bed Price</label>
                                                                                            <div className="input-group">
                                                                                                <input
                                                                                                    type="number"
                                                                                                    className="form-control"
                                                                                                    name="childWithoutBedPrice"
                                                                                                    value={newRoom.childWithoutBedPrice}
                                                                                                    onChange={handleNewRoomChange}
                                                                                                    placeholder="Enter price"
                                                                                                    min="0"
                                                                                                />
                                                                                                <span className="input-group-text">
                                                                                                    {getCurrencySymbol(newRoom.currency)}
                                                                                                </span>
                                                                                            </div>
                                                                                        </div>
                                                                                        
                                                                                        <div className="col-12 d-flex gap-2 mt-2">
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-primary"
                                                                                                onClick={handleAddRoom}
                                                                                            >
                                                                                                <i className="bi bi-check me-1"></i>
                                                                                                Add Room
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-danger"
                                                                                                onClick={() => setShowAddRoomForm(false)}
                                                                                            >
                                                                                                Cancel
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        
                                                                        {/* Room Details Table - UPDATED: Removed date columns */}
                                                                        {roomDetails.length > 0 ? (
                                                                            <div className="table-responsive">
                                                                                <table className="table table-hover mb-0">
                                                                                    <thead className="table-light">
                                                                                        <tr>
                                                                                            <th>Room Type</th>
                                                                                            <th>Meal Plan</th>
                                                                                            <th>Price</th>
                                                                                            <th>Extra Bed</th>
                                                                                            <th>Child w/o Bed</th>
                                                                                            <th>Actions</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {roomDetails.map((room) => {
                                                                                            const currencySymbol = getCurrencySymbol(room.currency);
                                                                                            const mealPlanLabel = mealPlanOptions.find(mp => mp.value === room.mealPlan)?.label || room.mealPlan;
                                                                                            
                                                                                            return (
                                                                                                <tr key={room.id}>
                                                                                                    <td style={{ textTransform: 'uppercase' }}>{room.roomType}</td>
                                                                                                    <td>{mealPlanLabel}</td>
                                                                                                    <td>
                                                                                                        <strong>{currencySymbol}{room.price}</strong>
                                                                                                        <small className="text-muted ms-1">({room.currency})</small>
                                                                                                    </td>
                                                                                                    <td>
                                                                                                        {room.extraBedPrice ? (
                                                                                                            <span className="badge bg-success">
                                                                                                                {currencySymbol}{room.extraBedPrice}
                                                                                                            </span>
                                                                                                        ) : (
                                                                                                            <span className="badge bg-secondary">-</span>
                                                                                                        )}
                                                                                                    </td>
                                                                                                    <td>
                                                                                                        {room.childWithoutBedPrice ? (
                                                                                                            <span className="badge bg-info">
                                                                                                                {currencySymbol}{room.childWithoutBedPrice}
                                                                                                            </span>
                                                                                                        ) : (
                                                                                                            <span className="badge bg-secondary">-</span>
                                                                                                        )}
                                                                                                    </td>
                                                                                                    <td>
                                                                                                        <div className="btn-group btn-group-sm">
                                                                                                            <button
                                                                                                                type="button"
                                                                                                                className="btn btn-primary"
                                                                                                                onClick={() => handleEditRoom(room)}
                                                                                                            >
                                                                                                                <i className="bi bi-pencil"></i>
                                                                                                            </button>
                                                                                                            <button
                                                                                                                type="button"
                                                                                                                className="btn btn-danger"
                                                                                                                onClick={() => handleDeleteRoom(room.id)}
                                                                                                            >
                                                                                                                <i className="bi bi-trash"></i>
                                                                                                            </button>
                                                                                                        </div>
                                                                                                    </td>
                                                                                                </tr>
                                                                                            );
                                                                                        })}
                                                                                    </tbody>
                                                                                </table>
                                                                                
                                                                                {/* Show global validity dates note */}
                                                                                {globalValidFrom && globalValidTo && (
                                                                                    <div className="alert alert-info mt-3 mb-0">
                                                                                        <i className="bi bi-calendar-check me-2"></i>
                                                                                        <strong>Validity dates applied to all rooms:</strong> {formatDate(globalValidFrom)} to {formatDate(globalValidTo)}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-center py-5">
                                                                                <h6 className="text-muted">No room details added yet</h6>
                                                                                <p className="text-muted small">Click "Add Room" to add room details for this property</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Amenities Tab - Points System */}
                                                        {activeTab === "amenities" && (
                                                            <div className="tab-pane fade show active">
                                                                <div className="card border-primary">
                                                                    <div className="card-header bg-primary bg-opacity-10">
                                                                        <h6 className="mb-0">
                                                                            <i className="bi bi-wifi me-2"></i>
                                                                            Amenities - {selectedHotel.hotelName}
                                                                        </h6>
                                                                    </div>
                                                                    
                                                                    <div className="card-body">
                                                                        {/* Add Amenity Point Form */}
                                                                        <div className="card border-info mb-3">
                                                                            <div className="card-body">
                                                                                <h6 className="card-title text-info mb-3">Add New Amenity Point</h6>
                                                                                <div className="row g-3">
                                                                                    <div className="col-md-10">
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control"
                                                                                            value={newAmenityPoint}
                                                                                            onChange={(e) => setNewAmenityPoint(e.target.value)}
                                                                                            placeholder="Enter amenity point..."
                                                                                        />
                                                                                    </div>
                                                                                    <div className="col-md-2">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-primary w-100"
                                                                                            onClick={handleAddAmenityPoint}
                                                                                        >
                                                                                            <i className="bi bi-plus me-1"></i>
                                                                                            Add Point
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        {/* Amenities Points Display */}
                                                                        {amenities.length > 0 ? (
                                                                            <div className="card">
                                                                                <div className="card-body">
                                                                                    <h6 className="card-title mb-3">Amenities Points</h6>
                                                                                    <ol className="list-group list-group-numbered">
                                                                                        {amenities.map((point) => (
                                                                                            <li key={point.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                                                                <div className="ms-2 me-auto">
                                                                                                    <div className="fw-medium">{point.point}</div>
                                                                                                </div>
                                                                                                <div className="btn-group btn-group-sm">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-primary"
                                                                                                        onClick={() => handleEditAmenityPoint(point)}
                                                                                                    >
                                                                                                        <i className="bi bi-pencil"></i>
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-danger"
                                                                                                        onClick={() => handleDeleteAmenityPoint(point.id)}
                                                                                                    >
                                                                                                        <i className="bi bi-trash"></i>
                                                                                                    </button>
                                                                                                </div>
                                                                                            </li>
                                                                                        ))}
                                                                                    </ol>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-center py-5">
                                                                                <h6 className="text-muted">No amenities added yet</h6>
                                                                                <p className="text-muted small">Add points above to create amenities list</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Terms & Conditions Tab - Points System */}
                                                        {activeTab === "terms" && (
                                                            <div className="tab-pane fade show active">
                                                                <div className="card border-primary">
                                                                    <div className="card-header bg-primary bg-opacity-10">
                                                                        <h6 className="mb-0">
                                                                            <i className="bi bi-file-text me-2"></i>
                                                                            Terms & Conditions - {selectedHotel.hotelName}
                                                                        </h6>
                                                                    </div>
                                                                    
                                                                    <div className="card-body">
                                                                        {/* Add Term Point Form */}
                                                                        <div className="card border-info mb-3">
                                                                            <div className="card-body">
                                                                                <h6 className="card-title text-info mb-3">Add New Term & Condition Point</h6>
                                                                                <div className="row g-3">
                                                                                    <div className="col-md-10">
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control"
                                                                                            value={newTermPoint}
                                                                                            onChange={(e) => setNewTermPoint(e.target.value)}
                                                                                            placeholder="Enter term & condition point..."
                                                                                        />
                                                                                    </div>
                                                                                    <div className="col-md-2">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-primary w-100"
                                                                                            onClick={handleAddTermPoint}
                                                                                        >
                                                                                            <i className="bi bi-plus me-1"></i>
                                                                                            Add Point
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        {/* Terms & Conditions Points Display */}
                                                                        {termsAndConditions.length > 0 ? (
                                                                            <div className="card">
                                                                                <div className="card-body">
                                                                                    <h6 className="card-title mb-3">Terms & Conditions Points</h6>
                                                                                    <ol className="list-group list-group-numbered">
                                                                                        {termsAndConditions.map((point) => (
                                                                                            <li key={point.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                                                                <div className="ms-2 me-auto">
                                                                                                    <div className="fw-medium">{point.point}</div>
                                                                                                </div>
                                                                                                <div className="btn-group btn-group-sm">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-primary"
                                                                                                        onClick={() => handleEditTermPoint(point)}
                                                                                                    >
                                                                                                        <i className="bi bi-pencil"></i>
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-danger"
                                                                                                        onClick={() => handleDeleteTermPoint(point.id)}
                                                                                                    >
                                                                                                        <i className="bi bi-trash"></i>
                                                                                                    </button>
                                                                                                </div>
                                                                                            </li>
                                                                                        ))}
                                                                                    </ol>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-center py-5">
                                                                                <h6 className="text-muted">No terms & conditions added yet</h6>
                                                                                <p className="text-muted small">Add points above to create terms & conditions</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Cancellation Policy Tab - Points System */}
                                                        {activeTab === "cancellation" && (
                                                            <div className="tab-pane fade show active">
                                                                <div className="card border-primary">
                                                                    <div className="card-header bg-primary bg-opacity-10">
                                                                        <h6 className="mb-0">
                                                                            <i className="bi bi-calendar-event me-2"></i>
                                                                            Cancellation Policy - {selectedHotel.hotelName}
                                                                        </h6>
                                                                    </div>
                                                                    
                                                                    <div className="card-body">
                                                                        {/* Add Cancellation Point Form */}
                                                                        <div className="card border-info mb-3">
                                                                            <div className="card-body">
                                                                                <h6 className="card-title text-info mb-3">Add New Cancellation Policy Point</h6>
                                                                                <div className="row g-3">
                                                                                    <div className="col-md-10">
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control"
                                                                                            value={newCancellationPoint}
                                                                                            onChange={(e) => setNewCancellationPoint(e.target.value)}
                                                                                            placeholder="Enter cancellation policy point..."
                                                                                        />
                                                                                    </div>
                                                                                    <div className="col-md-2">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-primary w-100"
                                                                                            onClick={handleAddCancellationPoint}
                                                                                        >
                                                                                            <i className="bi bi-plus me-1"></i>
                                                                                            Add Point
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        {/* Cancellation Policy Points Display */}
                                                                        {cancellationPolicy.length > 0 ? (
                                                                            <div className="card">
                                                                                <div className="card-body">
                                                                                    <h6 className="card-title mb-3">Cancellation Policy Points</h6>
                                                                                    <ol className="list-group list-group-numbered">
                                                                                        {cancellationPolicy.map((point) => (
                                                                                            <li key={point.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                                                                <div className="ms-2 me-auto">
                                                                                                    <div className="fw-medium">{point.point}</div>
                                                                                                </div>
                                                                                                <div className="btn-group btn-group-sm">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-primary"
                                                                                                        onClick={() => handleEditCancellationPoint(point)}
                                                                                                    >
                                                                                                        <i className="bi bi-pencil"></i>
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-danger"
                                                                                                        onClick={() => handleDeleteCancellationPoint(point.id)}
                                                                                                    >
                                                                                                        <i className="bi bi-trash"></i>
                                                                                                    </button>
                                                                                                </div>
                                                                                            </li>
                                                                                        ))}
                                                                                    </ol>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-center py-5">
                                                                                <h6 className="text-muted">No cancellation policy added yet</h6>
                                                                                <p className="text-muted small">Add points above to create cancellation policy</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Child Policy Tab - Points System */}
                                                        {activeTab === "childPolicy" && (
                                                            <div className="tab-pane fade show active">
                                                                <div className="card border-primary">
                                                                    <div className="card-header bg-primary bg-opacity-10">
                                                                        <h6 className="mb-0">
                                                                            <i className="bi bi-person me-2"></i>
                                                                            Child Policy - {selectedHotel.hotelName}
                                                                        </h6>
                                                                    </div>
                                                                    
                                                                    <div className="card-body">
                                                                        {/* Add Child Policy Point Form */}
                                                                        <div className="card border-info mb-3">
                                                                            <div className="card-body">
                                                                                <h6 className="card-title text-info mb-3">Add New Child Policy Point</h6>
                                                                                <div className="row g-3">
                                                                                    <div className="col-md-10">
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control"
                                                                                            value={newChildPolicyPoint}
                                                                                            onChange={(e) => setNewChildPolicyPoint(e.target.value)}
                                                                                            placeholder="Enter child policy point..."
                                                                                        />
                                                                                    </div>
                                                                                    <div className="col-md-2">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-primary w-100"
                                                                                            onClick={handleAddChildPolicyPoint}
                                                                                        >
                                                                                            <i className="bi bi-plus me-1"></i>
                                                                                            Add Point
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        {/* Child Policy Points Display */}
                                                                        {childPolicy.length > 0 ? (
                                                                            <div className="card">
                                                                                <div className="card-body">
                                                                                    <h6 className="card-title mb-3">Child Policy Points</h6>
                                                                                    <ol className="list-group list-group-numbered">
                                                                                        {childPolicy.map((point) => (
                                                                                            <li key={point.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                                                                <div className="ms-2 me-auto">
                                                                                                    <div className="fw-medium">{point.point}</div>
                                                                                                </div>
                                                                                                <div className="btn-group btn-group-sm">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-primary"
                                                                                                        onClick={() => handleEditChildPolicyPoint(point)}
                                                                                                    >
                                                                                                        <i className="bi bi-pencil"></i>
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-danger"
                                                                                                        onClick={() => handleDeleteChildPolicyPoint(point.id)}
                                                                                                    >
                                                                                                        <i className="bi bi-trash"></i>
                                                                                                    </button>
                                                                                                </div>
                                                                                            </li>
                                                                                        ))}
                                                                                    </ol>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-center py-5">
                                                                                <h6 className="text-muted">No child policy added yet</h6>
                                                                                <p className="text-muted small">Add points above to create child policy</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Bank Details Tab - Updated */}
                                                        {activeTab === "bankDetails" && (
                                                            <div className="tab-pane fade show active">
                                                                <div className="card border-primary">
                                                                    <div className="card-header bg-primary bg-opacity-10 d-flex justify-content-between align-items-center">
                                                                        <h6 className="mb-0">
                                                                            <i className="bi bi-bank me-2"></i>
                                                                            Bank Details - {selectedHotel.hotelName}
                                                                        </h6>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-primary"
                                                                            onClick={() => setShowAddBankForm(!showAddBankForm)}
                                                                        >
                                                                            <i className="bi bi-plus me-1"></i>
                                                                            Add Bank Account
                                                                        </button>
                                                                    </div>
                                                                    
                                                                    <div className="card-body">
                                                                        {/* Add Bank Form - Updated */}
                                                                        {showAddBankForm && (
                                                                            <div className="card border-info mb-3">
                                                                                <div className="card-body">
                                                                                    <h6 className="card-title text-info mb-3">Add Bank Account</h6>
                                                                                    <div className="row g-3">
                                                                                        <div className="col-md-4">
                                                                                            <label className="form-label">Bank Name *</label>
                                                                                            <input
                                                                                                type="text"
                                                                                                className="form-control"
                                                                                                name="bankName"
                                                                                                value={newBankDetail.bankName}
                                                                                                onChange={handleBankDetailChange}
                                                                                                placeholder="e.g., State Bank of India"
                                                                                                required
                                                                                            />
                                                                                        </div>
                                                                                        <div className="col-md-4">
                                                                                            <label className="form-label">Account Number *</label>
                                                                                            <input
                                                                                                type="text"
                                                                                                className="form-control"
                                                                                                name="accountNumber"
                                                                                                value={newBankDetail.accountNumber}
                                                                                                onChange={handleBankDetailChange}
                                                                                                placeholder="e.g., 1234567890"
                                                                                                required
                                                                                            />
                                                                                        </div>
                                                                                        <div className="col-md-4">
                                                                                            <label className="form-label">SWIFT/IFSC Code</label>
                                                                                            <input
                                                                                                type="text"
                                                                                                className="form-control"
                                                                                                name="swiftIfscCode"
                                                                                                value={newBankDetail.swiftIfscCode}
                                                                                                onChange={handleBankDetailChange}
                                                                                                placeholder="e.g., SBIN0001234 or SBININBB123"
                                                                                            />
                                                                                        </div>
                                                                                        <div className="col-12 d-flex gap-2">
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-primary"
                                                                                                onClick={handleAddBankDetail}
                                                                                            >
                                                                                                <i className="bi bi-check me-1"></i>
                                                                                                Add Account
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-danger"
                                                                                                onClick={() => setShowAddBankForm(false)}
                                                                                            >
                                                                                                Cancel
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        
                                                                        {/* Bank Details Display - Updated */}
                                                                        {bankDetails.length > 0 ? (
                                                                            <div className="row">
                                                                                {bankDetails.map((bank) => (
                                                                                    <div key={bank.id} className="col-md-6 mb-3">
                                                                                        <div className="card h-100">
                                                                                            <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                                                                                <h6 className="mb-0">{bank.bankName}</h6>
                                                                                                <div className="btn-group btn-group-sm">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-primary"
                                                                                                        onClick={() => handleEditBankDetail(bank)}
                                                                                                    >
                                                                                                        <i className="bi bi-pencil"></i>
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-danger"
                                                                                                        onClick={() => handleDeleteBankDetail(bank.id)}
                                                                                                    >
                                                                                                        <i className="bi bi-trash"></i>
                                                                                                    </button>
                                                                                                </div>
                                                                                            </div>
                                                                                            <div className="card-body">
                                                                                                <div className="mb-2">
                                                                                                    <small className="text-muted">Account Number</small>
                                                                                                    <p className="mb-1 fw-medium">{bank.accountNumber}</p>
                                                                                                </div>
                                                                                                {bank.swiftIfscCode && (
                                                                                                    <div className="mb-2">
                                                                                                        <small className="text-muted">SWIFT/IFSC Code</small>
                                                                                                        <p className="mb-1 fw-medium">{bank.swiftIfscCode}</p>
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-center py-5">
                                                                                <h6 className="text-muted">No bank details added yet</h6>
                                                                                <p className="text-muted small">Click "Add Bank Account" to add bank details for this property</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Initial State */}
                                        {!showResults && (
                                            <div className="text-center py-5">
                                                <div className="display-1 text-muted mb-3">
                                                    <i className="bi bi-building" style={{ fontSize: '48px' }}></i>
                                                </div>
                                                <h4 className="mb-3">Search Your Properties</h4>
                                                <p className="text-muted">Type hotel name above to see suggestions</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Render the Preview Modal */}
            {showPreviewModal && <PreviewModal />}
            
            {/* Custom CSS */}
            <style jsx>{`
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                    padding: 20px;
                }
                
                .modal-container {
                    background-color: white;
                    border-radius: 12px;
                    width: 100%;
                    max-width: 1200px;
                    max-height: 90vh;
                    overflow: hidden;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
                }
                
                .nav-tabs-custom .nav-link {
                    color: #495057;
                    border: 1px solid transparent;
                    border-top-left-radius: 0.25rem;
                    border-top-right-radius: 0.25rem;
                    padding: 0.75rem 1.25rem;
                    font-weight: 500;
                    transition: all 0.3s ease;
                }
                
                .nav-tabs-custom .nav-link:hover {
                    border-color: #e9ecef #e9ecef #dee2e6;
                    color: #0d6efd;
                }
                
                .nav-tabs-custom .nav-link.active {
                    color: #0d6efd;
                    background-color: #fff;
                    border-color: #dee2e6 #dee2e6 #fff;
                    border-bottom: 3px solid #0d6efd;
                }
                
                .tab-content {
                    padding-top: 1rem;
                }
                
                .hotel-card:hover {
                    border-color: #0d6efd !important;
                }
                
                .dropdown-item:hover {
                    background-color: #f8f9fa;
                }
                
                .cursor-pointer {
                    cursor: pointer;
                }
                
                .position-relative {
                    position: relative;
                }
                
                .position-absolute {
                    position: absolute;
                }
                
                @media (max-width: 768px) {
                    .nav-tabs-custom .nav-link {
                        padding: 0.5rem;
                        font-size: 0.85rem;
                    }
                    
                    .room-form-row .col-md-2,
                    .room-form-row .col-md-3 {
                        margin-bottom: 10px;
                    }
                    
                    .modal-container {
                        margin: 10px;
                        max-height: 95vh;
                    }
                }
            `}</style>
        </div>
    );
};

export default HotelsMasterSearch;